/**
 * Frontend parser for ACTION_CARDS blocks embedded in LLM output.
 *
 * Priority:
 *   1. If `metadataCards` is provided (from `message.metadata.actionCards`), use it directly.
 *   2. Otherwise, extract the inline `<ACTION_CARDS>JSON</ACTION_CARDS>` block from content.
 *
 * Validation rules (must all pass for cards to be non-null):
 *   - 0 to 4 cards in the array (engine contract — see STO-56, teto subido para 4 na STO-148)
 *   - Each card has a non-empty string `label` and `standard_message`
 *
 * 2026-09-10 (STO-54, item 3) — este módulo virou o portão ÚNICO dos cards de
 * ação, e passou a FALAR quando recusa.
 *
 * O que ele conserta, medido no chat `964e465a`: a metade VIVA passava por aqui
 * e a metade do HISTÓRICO não passava por portão nenhum
 * (`persisted-presentation.ts` copiava o payload cru). O MESMO turno com uma
 * `category` desconhecida virava NADA ao vivo e TRÊS BOTÕES depois de recarregar
 * — e um card sem `label` derrubava a bolha inteira em `ActionCard.tsx:36`,
 * porque ninguém nunca tinha conferido a forma do que veio do banco.
 *
 * As regras que este portão segue, e a diferença entre elas importa:
 *
 * - **Tipo (categoria) desconhecido NÃO derruba o card.** O rótulo e a mensagem
 *   continuam válidos, então o card continua na tela e continua clicável — o
 *   recuo é VISÍVEL, que é o que a régua pede. Ele só não ganha o selo
 *   "Recomendado", que é exclusivo de `strategic`.
 * - **Payload recusado é REGISTRADO.** Antes ele sumia calado nas duas metades.
 *
 * 2026-09-11 (STO-56) — a exigência de "exatamente 3, um `strategic`" (regra do
 * Sprint 1A, 2026-05-20) ficou presa no tempo: o motor mudou o contrato em
 * `action-cards-payload.ts` (`axiomgo-engine` PR #1347, 2026-08-10, reafirmado
 * em `aa9c523c` de 2026-09-07) para `z.array(actionCardSchema).max(3)` — 0, 1,
 * 2 ou 3 cards são resposta legítima, e `category` DESCREVE o card em vez de
 * cotar uma categoria obrigatória. Este portão exigia 3 e pelo menos um
 * `strategic`; quando o motor mandava 2 (medido ao vivo, turno "olá" e turno
 * "quais equipamentos estão sem plano de preventiva?"), o portão jogava os DOIS
 * fora e o técnico ficava sem botão nenhum. A régua daqui passa a espelhar o
 * teto do motor (até 3), não mais uma contagem fixa nem uma cota de categoria.
 *
 * O que este módulo NÃO faz: inventar categoria. Um tipo que a tela não conhece
 * chega até o `data-category` como o motor mandou — normalizar seria a tela
 * mentindo sobre o que recebeu, e é justamente o rastro que o Sentry precisa.
 */
import * as Sentry from '@sentry/react'

export const ACTION_CARD_CATEGORIES = ['strategic', 'alternative', 'pointwise'] as const

export type ActionCardCategory = (typeof ACTION_CARD_CATEGORIES)[number]

/** De qual metade da conversa o payload veio — vira etiqueta no Sentry. */
export type OrigemDosCards = 'ao-vivo' | 'historico'

export interface ActionCard {
  label: string
  category: ActionCardCategory
  standard_message: string
  next_wow: string | null
}

export interface ParseResult {
  content: string
  cards: ActionCard[] | null
}

const BLOCK_RE = /\n*<ACTION_CARDS>\s*([\s\S]*?)\s*<\/ACTION_CARDS>\s*$/

/**
 * Teto de cards por turno — contrato do motor (`action-cards-payload.ts`): 0 a 4.
 *
 * STO-148 (ordem do Denis, 11/09/2026, opção (c) da STO-122): subiu de 3 para 4
 * nos DOIS repos. Esta tela sobe ANTES do motor (STO-130) porque este portão é
 * fail-closed — payload acima do teto derruba a FILEIRA INTEIRA, não só o
 * excedente. Enquanto o teto daqui ficasse em 3, todo turno que o motor emitisse
 * com 4 (ex.: `corrective.hub`, STO-104) chegava com ZERO botão ao operador.
 */
const MAX_CARDS_POR_TURNO = 4

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

function textoNaoVazio(value: unknown): value is string {
  return typeof value === 'string' && value.length > 0
}

function categoriaConhecida(value: unknown): value is ActionCardCategory {
  return ACTION_CARD_CATEGORIES.includes(value as ActionCardCategory)
}

/**
 * O registro que faltava. Sem ele o descarte era MUDO nas duas metades: o
 * usuário perdia os botões e não sobrava uma linha dizendo por quê.
 */
function reportar(tag: string, origem: OrigemDosCards, motivo: string): void {
  Sentry.captureMessage(tag, {
    level: 'warning',
    tags: { action_cards_origem: origem, action_cards_motivo: motivo },
  })
}

/**
 * Confere a FORMA de um card. Devolve `null` quando o objeto não serve.
 *
 * `next_wow` ausente vira `null` em vez de reprovar o turno: o campo é opcional
 * no motor e histórico antigo não o tem. Ausência de campo opcional não é
 * defeito — é histórico velho, e derrubar o turno por isso seria o produto
 * apagando card bom.
 */
function cardComForma(bruto: unknown, origem: OrigemDosCards): ActionCard | null {
  if (!isRecord(bruto)) return null
  if (!textoNaoVazio(bruto['label'])) return null
  if (!textoNaoVazio(bruto['standard_message'])) return null

  const categoria = bruto['category']
  if (!categoriaConhecida(categoria)) {
    // O card SOBREVIVE: rótulo e mensagem valem, só o tipo é que a tela não
    // conhece. Some do selo "Recomendado", não da tela.
    reportar('action_cards_categoria_desconhecida', origem, String(categoria))
  }

  return {
    label: bruto['label'],
    category: categoria as ActionCardCategory,
    standard_message: bruto['standard_message'],
    next_wow: typeof bruto['next_wow'] === 'string' ? bruto['next_wow'] : null,
  }
}

/**
 * O PORTÃO ÚNICO. As duas metades da conversa passam por aqui — a viva pelo
 * `parseActionCards` e a recarregada pelo `presentationFromPersistedMetadata` —
 * e é por isso que elas não podem mais divergir.
 *
 * Veredito sobre `payload.length === 0` (STO-56, dispatch de 2026-09-11): é LOTE
 * VÁLIDO, não recusa. O próprio motor descreve isso como resposta legítima —
 * `action-cards-structured.ts:267`: "lista vazia se não houver próximo passo
 * plausível". `sanitizeActionCards([], origem)` devolve `[]`, não `null`, e
 * `ActionCardList` (`ActionCardList.tsx:31`) já trata `cards.length === 0` como
 * "não renderiza nada" — o mesmo resultado visível de `null`, sem precisar
 * tratar 0 como forma de recusa aqui.
 */
export function sanitizeActionCards(payload: unknown, origem: OrigemDosCards): ActionCard[] | null {
  if (!Array.isArray(payload)) return null
  if (payload.length > MAX_CARDS_POR_TURNO) {
    reportar('action_cards_payload_recusado', origem, `quantidade:${payload.length}`)
    return null
  }

  const cards: ActionCard[] = []
  for (const bruto of payload) {
    const card = cardComForma(bruto, origem)
    if (card === null) {
      reportar('action_cards_payload_recusado', origem, 'forma-invalida')
      return null
    }
    cards.push(card)
  }

  return cards
}

/**
 * Parse action cards from an LLM response.
 *
 * @param raw - The raw message content string (may contain inline block)
 * @param metadataCards - Pre-extracted cards from message metadata (preferred over inline)
 */
export function parseActionCards(raw: string, metadataCards?: ActionCard[] | null): ParseResult {
  // Metadata from server takes priority — skip inline parsing entirely
  if (metadataCards != null && metadataCards.length > 0) {
    return { content: raw.trim(), cards: sanitizeActionCards(metadataCards, 'ao-vivo') }
  }

  // Try to extract inline block
  const match = raw.match(BLOCK_RE)
  if (!match) return { content: raw.trim(), cards: null }

  const content = raw.replace(BLOCK_RE, '').trim()

  try {
    const parsed = JSON.parse(match[1]) as unknown
    return { content, cards: sanitizeActionCards(parsed, 'ao-vivo') }
  } catch {
    // JSON quebrado dentro do bloco: o motor mandou algo que nem é payload.
    reportar('action_cards_payload_recusado', 'ao-vivo', 'json-invalido')
    return { content, cards: null }
  }
}
